import React, { useEffect, useState } from 'react';
import {
  Table, Tag, Select, Button, Space, Avatar, Modal, Input, message, Typography, Tooltip, DatePicker,
} from 'antd';
import { UserOutlined } from '@ant-design/icons';
import request from '../utils/request';
import { fmtTime } from '../utils/format';

const { RangePicker } = DatePicker;

const { Text } = Typography;

const money = (v: any) => `¥${Number(v ?? 0).toFixed(2)}`;

const statusMap: Record<string, { color: string; text: string }> = {
  PENDING: { color: 'orange', text: '待处理' },
  PAID: { color: 'green', text: '已打款' },
  REJECTED: { color: 'red', text: '已驳回' },
};

// 提现管理：用户申请提现后生成工单，线下打款，后台标记已打款/驳回
const Withdraws: React.FC = () => {
  const [list, setList] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState<string | undefined>('PENDING');
  const [statusCount, setStatusCount] = useState<Record<string, number>>({});
  const [pageSize, setPageSize] = useState(10);
  const [kwInput, setKwInput] = useState('');
  const [keyword, setKeyword] = useState('');
  const [range, setRange] = useState<any>(null);
  // 处理弹窗：记录目标单 + 处理结果 + 备注
  const [target, setTarget] = useState<any>(null);
  const [result, setResult] = useState<'PAID' | 'REJECTED'>('PAID');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchList = async (p = page) => {
    setLoading(true);
    try {
      const res: any = await request.get('/distribution/admin/withdraws', {
        params: {
          page: p,
          pageSize,
          status: status || undefined,
          keyword: keyword || undefined,
          startDate: range?.[0]?.format('YYYY-MM-DD'),
          endDate: range?.[1]?.format('YYYY-MM-DD'),
        },
      });
      setList(res.list);
      setTotal(res.total);
      setStatusCount(res.statusCount || {});
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [page, status, keyword, range, pageSize]);

  const resetFilters = () => {
    setKwInput(''); setKeyword(''); setRange(null); setStatus('PENDING'); setPage(1);
  };

  const openReview = (record: any, r: 'PAID' | 'REJECTED') => {
    setTarget(record);
    setResult(r);
    setNote('');
  };

  const submitReview = async () => {
    if (!note.trim()) {
      message.warning(result === 'PAID' ? '请填写打款备注（渠道/流水尾号）' : '请填写驳回原因');
      return;
    }
    setSubmitting(true);
    try {
      await request.put(`/distribution/admin/withdraws/${target.id}/review`, {
        result,
        note: note.trim(),
      });
      message.success(result === 'PAID' ? '已标记打款完成' : '已驳回，金额退回余额');
      setTarget(null);
      fetchList();
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '申请人', width: 200,
      render: (_: any, r: any) => (
        <Space size={4}>
          <Avatar size="small" icon={<UserOutlined />} />
          {r.user?.realName || r.user?.nickname || `用户#${r.userId}`}
          {r.user?.phone && <Text type="secondary">({r.user.phone})</Text>}
        </Space>
      ),
    },
    {
      title: '提现金额', dataIndex: 'amount', width: 110,
      render: (v: any) => <Tag color="gold" style={{ fontSize: 13 }}>{money(v)}</Tag>,
    },
    {
      title: '收款账户', dataIndex: 'payInfo', width: 200, ellipsis: true,
      render: (v: string) => (
        <Tooltip title={v}><span>{v || '-'}</span></Tooltip>
      ),
    },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (v: string) => <Tag color={statusMap[v]?.color}>{statusMap[v]?.text || v}</Tag>,
    },
    { title: '申请时间', dataIndex: 'createdAt', width: 160, render: (v: string) => fmtTime(v) },
    {
      title: '处理备注', width: 180, ellipsis: true,
      render: (_: any, r: any) => {
        if (r.status === 'PENDING') return <Text type="secondary">-</Text>;
        return (
          <Tooltip title={`${r.adminNote || ''}${r.reviewer ? `（${r.reviewer.name || r.reviewer.username} ${fmtTime(r.updatedAt)}）` : ''}`}>
            <span>{r.adminNote || '-'}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '操作', width: 180, fixed: 'right' as const,
      render: (_: any, r: any) =>
        r.status === 'PENDING' ? (
          <>
            <Button size="small" type="primary" onClick={() => openReview(r, 'PAID')}>已打款</Button>{' '}
            <Button size="small" danger onClick={() => openReview(r, 'REJECTED')}>驳回</Button>
          </>
        ) : (
          <Text type="secondary">{r.status === 'PAID' ? `打款于 ${fmtTime(r.paidAt)}` : '已驳回'}</Text>
        ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>
          提现管理
          {(statusCount.PENDING || 0) > 0 && (
            <Tag color="orange" style={{ marginLeft: 12, fontSize: 13 }}>待处理 {statusCount.PENDING} 笔</Tag>
          )}
        </h2>
        <Space>
          <Input.Search
            allowClear
            placeholder="搜索申请人昵称/姓名/手机号"
            style={{ width: 240 }}
            value={kwInput}
            onChange={(e) => setKwInput(e.target.value)}
            onSearch={(v) => { setPage(1); setKeyword(v.trim()); }}
          />
          <RangePicker
            placeholder={['申请起', '申请止']}
            onChange={(v) => { setPage(1); setRange(v); }}
          />
          <Select allowClear placeholder="筛选状态" style={{ width: 140 }} value={status}
            onChange={(v) => { setStatus(v); setPage(1); }}
            options={[
              { label: '待处理', value: 'PENDING' },
              { label: '已打款', value: 'PAID' },
              { label: '已驳回', value: 'REJECTED' },
            ]} />
          {(keyword || range || status !== 'PENDING') && <Button onClick={resetFilters}>重置</Button>}
        </Space>
      </div>

      <Table columns={columns} dataSource={list} rowKey="id" loading={loading} scroll={{ x: 1200 }}
        pagination={{
          current: page, total, pageSize, showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }} />

      <Modal
        title={result === 'PAID' ? '标记已打款' : '驳回提现申请'}
        open={!!target}
        onCancel={() => setTarget(null)}
        onOk={submitReview}
        confirmLoading={submitting}
        okText={result === 'PAID' ? '确认已打款' : '确认驳回'}
        okButtonProps={{ danger: result === 'REJECTED' }}
        destroyOnClose
      >
        {target && (
          <div style={{ marginBottom: 12 }}>
            用户 <b>{target.user?.realName || target.user?.nickname || `用户#${target.userId}`}</b> 申请提现{' '}
            <Tag color="gold">{money(target.amount)}</Tag>
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">收款账户：</Text>{target.payInfo}
            </div>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {result === 'PAID'
                  ? '请先确认已完成线下转账。标记后该笔金额计入累计已核销。'
                  : '驳回后提现金额将退回用户佣金余额，并生成一条冲正流水。'}
              </Text>
            </div>
          </div>
        )}
        <Input.TextArea
          rows={2}
          maxLength={200}
          placeholder={result === 'PAID' ? '打款备注，如：微信转账 流水尾号1234' : '驳回原因，将通知用户'}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </Modal>
    </div>
  );
};

export default Withdraws;
